import React, { useState, useEffect } from 'react';
import './ImageSlider.css'; // Import the dedicated CSS file

// The main ImageSlider component
const ImageSlider = ({ slides = [] }) => {
  // State to manage the currently checked slide, defaulting to the middle one if available
  const [checkedSlide, setCheckedSlide] = useState(
    slides.length > 0 ? slides[Math.floor(slides.length / 2)].id : null
  );

  // Effect to update the checked slide if the slides prop changes
  // This ensures the slider initializes correctly even if slides are loaded asynchronously
  useEffect(() => {
    if (slides.length > 0 && !checkedSlide) {
      setCheckedSlide(slides[Math.floor(slides.length / 2)].id);
    }
  }, [slides, checkedSlide]);

  // If no slides are provided, render a fallback message
  if (slides.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-500 text-lg rounded-lg border border-dashed border-gray-300 bg-white shadow-sm mx-auto max-w-md">
        No slides to display. Please provide an array of slide objects.
      </div>
    );
  }

  // Generate dynamic CSS variables for each slide's background/image
  // This is a more robust way to handle dynamic styles when CSS is externalized
  const dynamicSlideStyles = slides.map((slide) => ({
    [`--slide-${slide.id}-background`]: slide.background || '#ccc',
    [`--slide-${slide.id}-image`]: slide.image ? `url(${slide.image})` : 'none',
  }));

  // Combine all dynamic styles into a single style object for the slider section
  const sliderCustomProperties = dynamicSlideStyles.reduce((acc, current) => ({ ...acc, ...current }), {});

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4 font-inter">
      <section id="slider" style={sliderCustomProperties}> {/* Apply dynamic CSS variables here */}
        {/* Render radio buttons for each slide */}
        {slides.map((slide) => (
          <React.Fragment key={`radio-${slide.id}`}>
            <input
              type="radio"
              name="slider"
              id={`s${slide.id}`}
              checked={checkedSlide === slide.id}
              onChange={() => setCheckedSlide(slide.id)}
            />
          </React.Fragment>
        ))}

        {/* Render labels (visual slides) for each slide */}
        {slides.map((slide, index) => (
          <label
            key={`label-${slide.id}`}
            htmlFor={`s${slide.id}`}
            id={`slide${slide.id}`}
            className={`rounded-xl shadow-lg slide-${slide.id}`} // Add a class for specific slide styling
            aria-label={slide.alt || slide.content || `Slide ${slide.id}`} // Accessibility: provides descriptive text for screen readers
          >
            {/* Display slide content or a default text */}
            {slide.content || `Slide ${slide.id}`}
          </label>
        ))}
      </section>
    </div>
  );
};

export default ImageSlider;
